import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Upload } from "lucide-react";
import api from "@/lib/api";

const FIELDS = [
  { k: "site_name", label: "Site name", placeholder: "HK Forexedge" },
  { k: "slogan", label: "Slogan", placeholder: "Algorithmic precision. Human edge." },
  {
    k: "meta_description",
    label: "Meta description",
    placeholder: "Premium trading indicators, EAs, signals and courses.",
    textarea: true,
  },
  { k: "contact_email", label: "Contact email" },
  { k: "telegram_url", label: "Telegram URL" },
  { k: "whatsapp_url", label: "WhatsApp URL" },
  { k: "footer_note", label: "Footer note" },
];

const ACCEPT = ".png,.jpg,.jpeg,.webp,.svg,.ico";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${api.defaults.baseURL || ""}`.replace(/\/api$/, "") + u;
  return u;
}

export default function AdminBranding() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => {
    api
      .get("/settings/branding")
      .then((r) => setForm(r.data || {}))
      .catch(() => toast.error("Failed to load branding"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = { ...form };
      for (const k of Object.keys(clean)) {
        if (clean[k] === "") clean[k] = null;
      }
      await api.put("/admin/settings/branding", clean);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind, field, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/settings/branding/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: r.data.url }));
      toast.success(`${kind === "logo" ? "Logo" : "Favicon"} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const AssetField = ({ kind, field, label, inputRef, note }) => (
    <div>
      <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-slate-400">
        {label}
      </label>
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-black/40">
          {form[field] ? (
            <img
              src={resolveUrl(form[field])}
              alt={label}
              data-testid={`branding-${field}-preview`}
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-[10px] text-slate-600">None</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            disabled={loading}
            value={form[field] || ""}
            onChange={(e) => setForm({ ...form, [field]: e.target.value })}
            placeholder={`Paste ${label.toLowerCase()} or upload`}
            data-testid={`branding-${field}`}
            className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            data-testid={`branding-${kind}-file`}
            onChange={(e) => {
              upload(kind, field, e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === kind}
            data-testid={`branding-${kind}-upload`}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-white/10 disabled:opacity-50"
          >
            {uploading === kind ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            Upload {kind === "logo" ? "logo" : "favicon"}
          </button>
          {note ? (
            <p className="text-[11px] leading-snug text-slate-500" data-testid={`branding-${kind}-note`}>
              {note}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-branding">
      <div className="mb-8">
        <p className="overline mb-2">Admin · Branding</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          Identity controls
        </h1>
        <p className="mt-2 max-w-xl text-sm text-slate-400">
          These values feed the storefront meta, logo, contact and support
          endpoints. They read publicly, but only admins can write.
        </p>
      </div>

      <form onSubmit={save} className="card-edge grid gap-5 p-6 lg:p-8">
        <div className="grid gap-5 md:grid-cols-2">
          <AssetField kind="logo" field="logo_url" label="Logo" inputRef={logoRef} note="Recommended: transparent PNG or SVG, at least 240px wide." />
          <AssetField kind="favicon" field="favicon_url" label="Favicon" inputRef={faviconRef} note="Tip: use a square image — 512×512 PNG or a 32×32 / 48×48 .ico — so it stays crisp in the browser tab." />
          {FIELDS.map((f) => (
            <div key={f.k} className={f.textarea ? "md:col-span-2" : ""}>
              <label className="mb-2 block text-xs font-medium uppercase tracking-widest text-slate-400">
                {f.label}
              </label>
              {f.textarea ? (
                <textarea
                  rows={3}
                  disabled={loading}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.placeholder}
                  data-testid={`branding-${f.k}`}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              ) : (
                <input
                  type="text"
                  disabled={loading}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.placeholder}
                  data-testid={`branding-${f.k}`}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            data-testid="branding-save"
            className="btn-edge"
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Save branding
          </button>
        </div>
      </form>
    </div>
  );
}
